import React from 'react';
import { motion } from 'framer-motion';
import { Database, Cloud, LayoutGrid, ShieldCheck, Share2, PieChart, ArrowRight, Activity, Server, Lock } from 'lucide-react';
export function ArchitectureDiagram() {
  return <section className="relative w-full bg-[#16120F] py-24 md:py-32 overflow-hidden min-h-[900px] flex items-center" data-id="element-207">
      {/* Background SVG Lines */}
      <div className="absolute inset-0 z-0 opacity-60" data-id="element-208">
        <svg className="w-full h-full" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg" data-id="element-209">
          <defs data-id="element-210">
            <linearGradient id="glow" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-211">
              <stop offset="0%" stopColor="#EA580C" stopOpacity="0" data-id="element-212" />
              <stop offset="50%" stopColor="#EA580C" stopOpacity="1" data-id="element-213" />
              <stop offset="100%" stopColor="#EA580C" stopOpacity="0" data-id="element-214" />
            </linearGradient>
            <filter id="blur" data-id="element-215">
              <feGaussianBlur stdDeviation="4" data-id="element-216" />
            </filter>
          </defs>

          {/* Base Lines */}
          <path d="M -100 400 L 200 400 L 300 200 L 500 200 L 600 400 L 800 400" fill="none" stroke="#332A24" strokeWidth="2" data-id="element-217" />
          <path d="M 500 200 L 700 200 L 800 100 L 1000 100" fill="none" stroke="#332A24" strokeWidth="2" data-id="element-218" />
          <path d="M 800 400 L 1000 400 L 1100 600 L 1300 600" fill="none" stroke="#332A24" strokeWidth="2" data-id="element-219" />
          <path d="M 400 700 L 600 700 L 700 500 L 800 500" fill="none" stroke="#332A24" strokeWidth="2" data-id="element-220" />
          <path d="M 800 500 L 800 700 L 1000 700" fill="none" stroke="#332A24" strokeWidth="2" data-id="element-221" />

          {/* Animated Glowing Lines */}
          <motion.path d="M -100 400 L 200 400 L 300 200 L 500 200" fill="none" stroke="url(#glow)" strokeWidth="3" filter="url(#blur)" initial={{
          pathLength: 0,
          opacity: 0
        }} animate={{
          pathLength: 1,
          opacity: 1
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-222" />
          <motion.path d="M 500 200 L 700 200 L 800 100 L 1000 100" fill="none" stroke="url(#glow)" strokeWidth="3" filter="url(#blur)" initial={{
          pathLength: 0,
          opacity: 0
        }} animate={{
          pathLength: 1,
          opacity: 1
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'linear',
          delay: 1
        }} data-id="element-223" />
          <motion.path d="M 800 400 L 1000 400 L 1100 600 L 1300 600" fill="none" stroke="url(#glow)" strokeWidth="3" filter="url(#blur)" initial={{
          pathLength: 0,
          opacity: 0
        }} animate={{
          pathLength: 1,
          opacity: 1
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'linear',
          delay: 2
        }} data-id="element-224" />
        </svg>
      </div>

      <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-10 w-full grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-12" data-id="element-225">
        {/* Left Content */}
        <div className="flex flex-col justify-center pt-10 lg:pt-0" data-id="element-226">
          <h2 className="text-5xl md:text-7xl font-title font-medium tracking-tight text-white leading-[1.05] mb-6" data-id="element-227">
            Connect
            <br data-id="element-228" />
            <span className="text-[#EA580C]" data-id="element-229">Everything.</span>
            <br data-id="element-230" />
            Architect
            <br data-id="element-231" />
            Anything.
          </h2>
          <p className="text-lg text-[#9C8E82] leading-relaxed mb-10 max-w-sm" data-id="element-232">
            The intelligent infrastructure platform that connects your data,
            systems, and teams—seamlessly.
          </p>
          <div className="flex items-center gap-6" data-id="element-233">
            <button className="px-6 py-3 rounded-full bg-[#EA580C] hover:bg-[#d24e09] text-white font-semibold transition-colors flex items-center gap-2" data-id="element-234">
              Request a Demo
              <ArrowRight size={18} data-id="element-235" />
            </button>
            <button className="text-[#EA580C] font-medium hover:text-white transition-colors flex items-center gap-2" data-id="element-236">
              Explore Platform
              <ArrowRight size={18} data-id="element-237" />
            </button>
          </div>
        </div>

        {/* Right Diagram Area */}
        <div className="relative h-[800px] hidden lg:block" data-id="element-238">
          {/* Data Sources Card */}
          <div className="absolute top-[50px] left-[100px] w-[280px] bg-[#221C18]/80 backdrop-blur-md border border-white/10 rounded-2xl p-5 shadow-2xl" data-id="element-239">
            <div className="flex items-center gap-2 mb-4 text-xs font-bold tracking-widest text-neutral-400 uppercase" data-id="element-240">
              <Database size={14} className="text-[#EA580C]" data-id="element-241" />
              Data Sources
            </div>
            <div className="space-y-2" data-id="element-242">
              <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5" data-id="element-243">
                <Cloud size={18} className="text-neutral-300" data-id="element-244" />
                <div data-id="element-245">
                  <div className="text-sm font-medium text-white" data-id="element-246">
                    Cloud Storage
                  </div>
                  <div className="text-xs text-neutral-500" data-id="element-247">Amazon S3</div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5" data-id="element-248">
                <Server size={18} className="text-neutral-300" data-id="element-249" />
                <div data-id="element-250">
                  <div className="text-sm font-medium text-white" data-id="element-251">Database</div>
                  <div className="text-xs text-neutral-500" data-id="element-252">PostgreSQL</div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5" data-id="element-253">
                <LayoutGrid size={18} className="text-neutral-300" data-id="element-254" />
                <div data-id="element-255">
                  <div className="text-sm font-medium text-white" data-id="element-256">
                    Applications
                  </div>
                  <div className="text-xs text-neutral-500" data-id="element-257">12 Connectors</div>
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between text-xs text-neutral-500 hover:text-white cursor-pointer transition-colors" data-id="element-258">
              View all sources <ArrowRight size={12} data-id="element-259" />
            </div>
          </div>

          {/* Data Warehouse Card */}
          <div className="absolute top-[250px] left-[450px] w-[300px] bg-[#221C18]/80 backdrop-blur-md border border-white/10 rounded-2xl p-5 shadow-2xl z-20" data-id="element-260">
            <div className="flex items-center gap-2 mb-4 text-xs font-bold tracking-widest text-neutral-400 uppercase" data-id="element-261">
              <Database size={14} className="text-[#EA580C]" data-id="element-262" />
              Data Warehouse
            </div>
            <div className="mb-6" data-id="element-263">
              <div className="text-sm font-medium text-white" data-id="element-264">
                Analytics Warehouse
              </div>
              <div className="text-xs text-neutral-500" data-id="element-265">Snowflake</div>
            </div>
            {/* Hex Grid Graphic Placeholder */}
            <div className="h-32 flex items-center justify-center mb-4" data-id="element-266">
              <div className="grid grid-cols-5 gap-2 opacity-80" data-id="element-267">
                {Array.from({
                length: 25
              }).map((_, i) => <div key={i} className={`w-3 h-3 rounded-full ${i % 3 === 0 ? 'bg-[#EA580C]' : 'bg-white/20'}`} data-id="element-268" />)}
              </div>
            </div>
            <div className="flex items-center justify-between text-xs" data-id="element-269">
              <div className="flex items-center gap-1.5 text-emerald-400" data-id="element-270">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" data-id="element-271" />
                Healthy
              </div>
              <div className="text-neutral-500" data-id="element-272">99.9% uptime</div>
            </div>
          </div>

          {/* Data Pipelines Card */}
          <div className="absolute top-[40px] right-[20px] w-[280px] bg-[#221C18]/80 backdrop-blur-md border border-white/10 rounded-2xl p-5 shadow-2xl" data-id="element-273">
            <div className="flex items-center gap-2 mb-4 text-xs font-bold tracking-widest text-neutral-400 uppercase" data-id="element-274">
              <Activity size={14} className="text-[#EA580C]" data-id="element-275" />
              Data Pipelines
            </div>
            <div className="space-y-2" data-id="element-276">
              <div className="p-3 rounded-xl bg-white/5 border border-white/5" data-id="element-277">
                <div className="text-sm font-medium text-white mb-1" data-id="element-278">
                  User Activity Pipeline
                </div>
                <div className="flex items-center justify-between" data-id="element-279">
                  <span className="text-xs text-[#EA580C]" data-id="element-280">Real-time</span>
                  <svg width="60" height="15" viewBox="0 0 60 15" fill="none" data-id="element-281">
                    <path d="M0 10 Q 10 0, 20 10 T 40 10 T 60 5" stroke="#EA580C" strokeWidth="1.5" fill="none" data-id="element-282" />
                  </svg>
                </div>
              </div>
              <div className="p-3 rounded-xl bg-white/5 border border-white/5" data-id="element-283">
                <div className="text-sm font-medium text-white mb-1" data-id="element-284">
                  ETL – Sales Data
                </div>
                <div className="flex items-center justify-between" data-id="element-285">
                  <span className="text-xs text-blue-400" data-id="element-286">Scheduled</span>
                  <svg width="60" height="15" viewBox="0 0 60 15" fill="none" data-id="element-287">
                    <path d="M0 5 Q 15 15, 30 5 T 60 10" stroke="#60A5FA" strokeWidth="1.5" fill="none" data-id="element-288" />
                  </svg>
                </div>
              </div>
              <div className="p-3 rounded-xl bg-white/5 border border-white/5" data-id="element-289">
                <div className="text-sm font-medium text-white mb-1" data-id="element-290">
                  Event Processing
                </div>
                <div className="flex items-center justify-between" data-id="element-291">
                  <span className="text-xs text-[#EA580C]" data-id="element-292">Streaming</span>
                  <svg width="60" height="15" viewBox="0 0 60 15" fill="none" data-id="element-293">
                    <path d="M0 12 Q 10 2, 20 12 T 40 8 T 60 12" stroke="#EA580C" strokeWidth="1.5" fill="none" data-id="element-294" />
                  </svg>
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between text-xs text-neutral-500 hover:text-white cursor-pointer transition-colors" data-id="element-295">
              View all pipelines <ArrowRight size={12} data-id="element-296" />
            </div>
          </div>

          {/* Governance Card */}
          <div className="absolute bottom-[100px] left-[150px] w-[260px] bg-[#221C18]/80 backdrop-blur-md border border-white/10 rounded-2xl p-5 shadow-2xl" data-id="element-297">
            <div className="flex items-center gap-2 mb-4 text-xs font-bold tracking-widest text-neutral-400 uppercase" data-id="element-298">
              <ShieldCheck size={14} className="text-[#EA580C]" data-id="element-299" />
              Governance
            </div>
            <div className="space-y-4" data-id="element-300">
              <div data-id="element-301">
                <div className="flex justify-between text-xs mb-1.5" data-id="element-302">
                  <span className="text-white" data-id="element-303">Data Quality</span>
                  <span className="text-neutral-400" data-id="element-304">98%</span>
                </div>
                <div className="h-1 w-full bg-white/10 rounded-full overflow-hidden" data-id="element-305">
                  <div className="h-full bg-[#EA580C] w-[98%]" data-id="element-306" />
                </div>
              </div>
              <div data-id="element-307">
                <div className="flex justify-between text-xs mb-1.5" data-id="element-308">
                  <span className="text-white" data-id="element-309">Compliance</span>
                  <span className="text-neutral-400" data-id="element-310">100%</span>
                </div>
                <div className="h-1 w-full bg-white/10 rounded-full overflow-hidden" data-id="element-311">
                  <div className="h-full bg-[#EA580C] w-full" data-id="element-312" />
                </div>
              </div>
              <div data-id="element-313">
                <div className="flex justify-between text-xs mb-1.5" data-id="element-314">
                  <span className="text-white" data-id="element-315">Access Control</span>
                  <span className="text-emerald-400" data-id="element-316">Active</span>
                </div>
                <div className="h-1 w-full bg-white/10 rounded-full overflow-hidden" data-id="element-317">
                  <div className="h-full bg-emerald-400 w-full" data-id="element-318" />
                </div>
              </div>
            </div>
            <div className="mt-5 flex items-center justify-between text-xs text-neutral-500 hover:text-white cursor-pointer transition-colors" data-id="element-319">
              View governance <ArrowRight size={12} data-id="element-320" />
            </div>
          </div>

          {/* Integrations Card */}
          <div className="absolute bottom-[40px] left-[480px] w-[240px] bg-[#221C18]/80 backdrop-blur-md border border-white/10 rounded-2xl p-5 shadow-2xl" data-id="element-321">
            <div className="flex items-center gap-2 mb-4 text-xs font-bold tracking-widest text-neutral-400 uppercase" data-id="element-322">
              <Share2 size={14} className="text-[#EA580C]" data-id="element-323" />
              Integrations
            </div>
            <div className="flex items-center gap-3 mb-4" data-id="element-324">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center" data-id="element-325">
                <img src="https://cdn.simpleicons.org/slack/white" alt="Slack" className="w-5 h-5" data-id="element-326" />
              </div>
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center" data-id="element-327">
                <img src="https://cdn.simpleicons.org/salesforce/white" alt="Salesforce" className="w-5 h-5" data-id="element-328" />
              </div>
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center" data-id="element-329">
                <LayoutGrid size={18} className="text-white" data-id="element-330" />
              </div>
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center" data-id="element-331">
                <Cloud size={18} className="text-white" data-id="element-332" />
              </div>
            </div>
            <div className="flex items-center justify-between text-xs text-neutral-500 hover:text-white cursor-pointer transition-colors" data-id="element-333">
              Manage integrations <ArrowRight size={12} data-id="element-334" />
            </div>
          </div>

          {/* Analytics & BI Card */}
          <div className="absolute bottom-[120px] right-[40px] w-[260px] bg-[#221C18]/80 backdrop-blur-md border border-white/10 rounded-2xl p-5 shadow-2xl" data-id="element-335">
            <div className="flex items-center gap-2 mb-4 text-xs font-bold tracking-widest text-neutral-400 uppercase" data-id="element-336">
              <PieChart size={14} className="text-[#EA580C]" data-id="element-337" />
              Analytics & BI
            </div>
            <div className="space-y-2" data-id="element-338">
              <div className="flex items-center justify-between p-2.5 rounded-xl bg-white/5 border border-white/5" data-id="element-339">
                <span className="text-sm text-white" data-id="element-340">Executive Dashboard</span>
                <div className="flex items-end gap-0.5 h-4" data-id="element-341">
                  <div className="w-1 bg-neutral-500 h-[40%]" data-id="element-342" />
                  <div className="w-1 bg-neutral-500 h-[70%]" data-id="element-343" />
                  <div className="w-1 bg-neutral-500 h-[50%]" data-id="element-344" />
                  <div className="w-1 bg-[#EA580C] h-[100%]" data-id="element-345" />
                </div>
              </div>
              <div className="flex items-center justify-between p-2.5 rounded-xl bg-white/5 border border-white/5" data-id="element-346">
                <span className="text-sm text-white" data-id="element-347">Customer Insights</span>
                <div className="flex items-end gap-0.5 h-4" data-id="element-348">
                  <div className="w-1 bg-[#EA580C] h-[80%]" data-id="element-349" />
                  <div className="w-1 bg-neutral-500 h-[40%]" data-id="element-350" />
                  <div className="w-1 bg-neutral-500 h-[60%]" data-id="element-351" />
                  <div className="w-1 bg-neutral-500 h-[30%]" data-id="element-352" />
                </div>
              </div>
              <div className="flex items-center justify-between p-2.5 rounded-xl bg-white/5 border border-white/5" data-id="element-353">
                <span className="text-sm text-white" data-id="element-354">Reports & Exports</span>
                <div className="flex items-end gap-0.5 h-4" data-id="element-355">
                  <div className="w-1 bg-neutral-500 h-[30%]" data-id="element-356" />
                  <div className="w-1 bg-neutral-500 h-[50%]" data-id="element-357" />
                  <div className="w-1 bg-[#EA580C] h-[90%]" data-id="element-358" />
                  <div className="w-1 bg-neutral-500 h-[60%]" data-id="element-359" />
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between text-xs text-neutral-500 hover:text-white cursor-pointer transition-colors" data-id="element-360">
              View all dashboards <ArrowRight size={12} data-id="element-361" />
            </div>
          </div>

          {/* Floating Icons / Nodes */}
          <div className="absolute top-[180px] left-[250px] w-10 h-10 rounded-full bg-[#EA580C]/20 border border-[#EA580C] flex items-center justify-center shadow-[0_0_20px_rgba(234,88,12,0.4)]" data-id="element-362">
            <Database size={16} className="text-[#EA580C]" data-id="element-363" />
          </div>
          <div className="absolute top-[200px] right-[320px] w-10 h-10 rounded-full bg-white/10 border border-white/30 flex items-center justify-center" data-id="element-364">
            <Lock size={16} className="text-white" data-id="element-365" />
          </div>
          <div className="absolute bottom-[300px] right-[280px] w-10 h-10 rounded-full bg-[#EA580C]/20 border border-[#EA580C] flex items-center justify-center shadow-[0_0_20px_rgba(234,88,12,0.4)]" data-id="element-366">
            <PieChart size={16} className="text-[#EA580C]" data-id="element-367" />
          </div>
          <div className="absolute bottom-[250px] left-[420px] w-12 h-12 rounded-full bg-[#EA580C] flex items-center justify-center shadow-[0_0_30px_rgba(234,88,12,0.6)] z-30" data-id="element-368">
            <Share2 size={20} className="text-white" data-id="element-369" />
          </div>
          <div className="absolute bottom-[280px] left-[80px] w-8 h-8 rounded-full bg-[#1A1410] border border-neutral-600 flex items-center justify-center z-30" data-id="element-370">
            <Lock size={12} className="text-neutral-400" data-id="element-371" />
          </div>
        </div>
      </div>
    </section>;
}